import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Card, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import CsvFileTypeImg from "assets/images/CsvFileType.webp";
import girlWithBook from "assets/images/girlWithBook.svg";
import XlsxFileTypeImg from "assets/images/XlsxFileType.webp";
import { GoogleIcon, MicrosoftIcon, UserCircleIcon } from "components";
import {
  useCustomerSearch,
  useGlobalStore,
  usePermissionStore,
} from "controller";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { PERMISSIONS } from "shared";
import { CreateTicketAutocomplete } from "shared/features/new-ticket/components/TicketAutocomplete";
import * as yup from "yup";

interface FormValues {
  customer: {
    mapping_id: string;
    customer_name: string;
    site_name: string;
    site_id: string;
    logo: string | null;
  } | null;
}

const schema = yup.object({
  customer: yup.object().nullable().required("Please select a customer"),
});
const cardContent = [
  {
    title: "Add users manually",
    description: "Create users individually by entering their details.",
    image: girlWithBook,
    isDisabled: false,
  },

  {
    title: "Bulk upload users",
    description: "Upload a .CSV or .XLSX file with user details.",
    fileType: [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    isDisabled: false,
  },
  {
    title: "Single sign-on (SSO)",
    description:
      "Allow users to sign in using existing identity providers such as Google or Microsoft Entra ID.",
    type: ["GOOGLE", "MICROSOFT"],
    isDisabled: false,
  },
];

const cardContent2 = [
  {
    title: "Add users manually",
    description: "Create users individually by entering their details.",
    image: girlWithBook,
    isDisabled: false,
  },
  {
    title: "Bulk Upload",
    description: "Upload .XLSX or .CSV file filled with required info.",
    fileType: [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    isDisabled: false,
  },
];

type IconType = "GOOGLE" | "MICROSOFT";

const ImageComponent = ({ image }: { image: string }) => {
  return (
    <Stack
      sx={{
        height: "100%",
        alignItems: "flex-end",
        justifyContent: "flex-end",
      }}
    >
      <img
        src={girlWithBook}
        style={{ width: "80%", height: "auto" }}
        alt="girl with book"
      />
    </Stack>
  );
};

const FileTypeComponent = () => (
  <Stack
    direction="row"
    gap="8px"
    alignSelf="flex-end"
    sx={{ paddingBottom: "12px", marginTop: "auto" }}
  >
    <img
      src={XlsxFileTypeImg}
      alt="XLSX file type"
      width="28px"
      height="32px"
    />
    <img src={CsvFileTypeImg} alt="CSV file type" width="28px" height="32px" />
  </Stack>
);

const TypeComponent = ({ fileType }: { fileType: IconType[] }) => (
  <Stack direction={"row"} pb="12px" gap={"8px"} ml="auto">
    <GoogleIcon sx={{ width: "34px", height: "26px" }} />
    <MicrosoftIcon sx={{ width: "34px", height: "26px" }} />
  </Stack>
);

function StepThreeAddUsers({
  onBoarding,
  onClose,
  setCurrentStep,
  setUserAddMethod,
  setSsoDrawerOpen,
  newCustomerData,
  isMspOnboarding,
  setOnBoardingState,
  setNewCustomerData,
}: {
  onBoarding?: string;
  onClose: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setUserAddMethod: React.Dispatch<React.SetStateAction<number | null>>;
  setSsoDrawerOpen: React.Dispatch<
    React.SetStateAction<{ open: boolean; mode: string; data: any }>
  >;
  newCustomerData: any;
  isMspOnboarding?: boolean;
  setOnBoardingState: any;
  setNewCustomerData: React.Dispatch<React.SetStateAction<any>>;
}) {
  const theme = useTheme();
  const [customerQuery, setCustomerQuery] = React.useState("");

  const { data: customers } = useCustomerSearch(customerQuery);
  const cards = useMemo(() => {
    if (onBoarding === "customer" && isMspOnboarding) {
      return cardContent2;
    }
    return cardContent;
  }, [isMspOnboarding]);

  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.CUSTOMER.CUSTOMER_ALL_RW]),
  );
  const customersPermissions = usePermissionStore((state) => state.customers);
  const defaultCustomerId = useGlobalStore((state) => state.defaultCustomer);

  const { control } = useForm<FormValues>({
    defaultValues: {
      customer:
        newCustomerData && Object.keys(newCustomerData).length > 0
          ? newCustomerData
          : null,
    },
    resolver: yupResolver(schema),
  });

  const customerDataList = useMemo(() => {
    if (Array.isArray(customers)) {
      if (haveFullAccess) {
        return customers.filter((item: any) => {
          return item?.mapping_id !== defaultCustomerId;
        });
      } else {
        return customers.filter((item: any) => {
          const customer = customersPermissions?.find(
            (customer: any) => customer?.customer_id === item?.mapping_id,
          );
          return (
            customer &&
            customer?.permission === "rw" &&
            item?.mapping_id !== defaultCustomerId
          );
        });
      }
    }
    return [];
  }, [customers, defaultCustomerId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        flex: 1,
        px: "32px",
      }}
    >
      <Stack gap={"24px"}>
        {/* heading content  */}
        <Stack gap="8px">
          <Typography variant="h4" sx={{ width: "356px" }}>
            {onBoarding === "customer"
              ? `Create users for ${newCustomerData?.customer_name}`
              : "Create users"}
          </Typography>
          <Typography variant="body-medium">
            Add users to start using DeskDay across Teams, web, desktop, and
            mobile.
          </Typography>
        </Stack>

        {onBoarding === "user" && (
          <Stack
            direction={"row"}
            sx={{
              gap: "24px",
              backgroundColor: theme.palette.neutral["005"],
              p: "12px 0px",
              borderRadius: "4px",
            }}
          >
            <CreateTicketAutocomplete
              name="customer"
              control={control}
              fullWidth
              options={customerDataList || []}
              label="Customer"
              nodataText="No customer found"
              placeholder="Search customer, site"
              required
              labelMappingKey="customer_name"
              onChange={(event: any, newValue: any) => {
                setNewCustomerData(newValue);
                // setCustomer(newValue);
                // setValue(newValue);
                // setFormValue("user", null);
              }}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : `${option.customer_name}${
                      option.site_name ? `, ${option.site_name}` : ""
                    }`
              }
              inputStyle={{
                color: theme.palette.lightBg.low,
              }}
              onInputChange={(event: any, newInputValue: any) => {
                setCustomerQuery(newInputValue);
              }}
              renderOptions={(props, option) => {
                return (
                  <li
                    {...props}
                    key={option.site_id}
                    style={{
                      ...theme.typography["body-small"],
                      display: "flex",
                      flexDirection: "row",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <UserCircleIcon fontSize="small" />
                    {option.customer_name}{" "}
                    {option.site_name ? `, ${option.site_name}` : ""}
                  </li>
                );
              }}
              isCreatable={haveFullAccess ? true : false}
              onCreateClick={() => {
                // setIsCreateCustomerDrawerOpen(true);
                setOnBoardingState("customer");
              }}
              createButtonText="Add New Customer"
            />
          </Stack>
        )}
        {/* List of Cards for add users  */}
        <Stack
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          {cards.map((data: any, index) => {
            if (data.isDisabled) {
              return null;
            }
            let SubComponent = null;
            if (data.image) {
              SubComponent = <ImageComponent image={data.image} />;
            } else if (data.fileType) {
              SubComponent = <FileTypeComponent />;
            } else if (data?.type) {
              SubComponent = (
                <TypeComponent
                  fileType={data.type.filter(
                    (type: any): type is IconType =>
                      type === "GOOGLE" || type === "MICROSOFT",
                  )}
                />
              );
            }
            const isDisabled = !newCustomerData?.mapping_id;
            return (
              <Tooltip
                key={index}
                title={isDisabled ? "Please select a customer" : ""}
                arrow
                placement="top"
                sx={{ width: "100%" }}
              >
                <Card
                  variant="outlined"
                  sx={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    width: "100%",
                    height: "148px",
                    boxShadow: isDisabled
                      ? "none"
                      : "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px,  rgba(0, 0, 0, 0.08) 0px 8px 1px -8px",
                    borderRadius: "8px",
                    transition: "box-shadow 0.3s ease",
                    ":hover": {
                      ...(!isDisabled && {
                        boxShadow: "none",
                      }),
                    },
                  }}
                  onClick={() => {
                    if (isDisabled) {
                      return;
                    }
                    if (index === 2) {
                      setSsoDrawerOpen({
                        open: true,
                        mode: "create",
                        data: null,
                      });
                    } else {
                      setCurrentStep(4);
                      setUserAddMethod(index + 1);
                    }
                  }}
                >
                  <Stack
                    direction={data.image ? "row" : "column"}
                    sx={{
                      justifyContent: "space-between",
                      display: "flex",
                      height: "100%",
                      padding: "16px 16px 4px 16px",
                    }}
                  >
                    <Stack>
                      <Typography variant="h6">{data.title}</Typography>
                      <Typography
                        variant="body-medium"
                        color={theme.palette.lightBg.low}
                      >
                        {data.description}
                      </Typography>
                    </Stack>
                    {SubComponent}
                  </Stack>
                </Card>
              </Tooltip>
            );
          })}
        </Stack>
      </Stack>

      {/* need help link  */}
      {onBoarding === "customer" && (
        <Stack
          direction={"row"}
          sx={{
            display: "flex",
            height: "100%",
            alignItems: "flex-end",
            justifyContent: "center",
            marginBottom: "24px",
          }}
          gap={"8px"}
        >
          <Typography
            variant="body-large"
            color={"#767B85"}
            className="imageCardTitle"
          >
            Need help?
          </Typography>
          <Typography
            className="imageCardTitle"
            variant="body-large"
            sx={{
              color: "#5563F5",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => {
              window.open(
                "https://support.deskday.com/en/articles/8236126-adding-a-user-manually-for-a-customer",
                "_blank",
              );
            }}
          >
            Learn how to add a new user
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default StepThreeAddUsers;
